import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import { client } from '..';
import { ADD_ASSET, GET_ALL_ASSETS, CHECKOUT_ASSET, GET_ASSET_ATTACHMENTS, UPDATE_ASSET, SOFT_DELETE_ASSET_RECORD } from '../Queries/asset';
import { setLoader } from './loaderSlice';

export const addAssetEntry = createAsyncThunk(
  'asset/addAssetEntry',
  async (asset, { rejectWithValue }) => {
    try {
      const { data } = await client.mutate({
        mutation: ADD_ASSET,
        variables: {
          ...asset,
          cost: asset.cost ? parseFloat(asset.cost) : null,
          assetPhoto: asset.assetPhoto || '',
        },
      });

      if (!data?.addAsset) throw new Error('No asset returned');

      return data.addAsset;
    } catch (error) {
      console.error("ADD_ASSET error:", error); // log full error
      return rejectWithValue(error.message || 'Failed to add asset entry');
    }
  }
);


export const getAllAssets = createAsyncThunk(
  'asset/getAllAssets',
  async ({ page, pageSize, ...filters }, { dispatch, rejectWithValue }) => {
    try {
      dispatch(setLoader(true));
      const { data } = await client.query({
        query: GET_ALL_ASSETS,
        variables: {
          pageSize,
          page,
          ...filters,
        },
        fetchPolicy: 'network-only',
      });
      const response = data?.getAllAssets || { records: [], totalCount: 0 };
      dispatch(setAllAssets(response));
      dispatch(setLoader(false));
      return data.getAllAssets;
    } catch (error) {
      dispatch(setLoader(false));
      return rejectWithValue(error.message || 'Failed to fetch assets');
    }
  }
);


export const getAllAssetAttachments = createAsyncThunk(
  'asset/getAllAssetAttachments',
  async (_, { dispatch, rejectWithValue }) => {
    try {
      const { data } = await client.query({
        query: GET_ASSET_ATTACHMENTS,
        fetchPolicy: 'network-only',
      });

      dispatch(setAssetAttachments(data.getAllAttachmentsOnly));
      return data.getAllAttachmentsOnly;
    } catch (error) {
      return rejectWithValue(error.message || 'Failed to fetch asset attachments');
    }
  }
);

export const checkoutAsset = createAsyncThunk(
  'asset/checkoutAsset',
  async (
    {
      id,
      productName,
      checkedOutBy,
      checkOutReason,
      expectedCheckInDate,
      checkOutDate,
      suppressCheckInStatus = false,
      isManuallyAddedToMaintenance = false
    },
    { dispatch, getState, rejectWithValue }
  ) => {
    try {
      const { data } = await client.mutate({
        mutation: CHECKOUT_ASSET,
        variables: {
          id,
          productName,
          checkedOutBy,
          checkOutReason,
          expectedCheckInDate,
          checkOutDate,
          suppressCheckInStatus,
          isManuallyAddedToMaintenance
        }
      });
      if (!data?.checkOutAsset) {
        return rejectWithValue('Checkout failed: No data returned');
      }
      const updatedAsset = data.checkOutAsset;
      const allAssetsFromState = getState().Assets?.allAssets;

      if (Array.isArray(allAssetsFromState)) {
        // State is plain array
        const updatedList = allAssetsFromState.map(asset =>
          asset.id === updatedAsset.id ? updatedAsset : asset
        );
        dispatch(setAllAssets(updatedList));
      }
      else if (allAssetsFromState?.rows) {
        const updatedRows = allAssetsFromState.rows.map(asset =>
          asset.id === updatedAsset.id ? updatedAsset : asset
        );
        dispatch(setAllAssets({
          ...allAssetsFromState,   
          rows: updatedRows        
        }));
      }

      return updatedAsset;
    } catch (error) {
      return rejectWithValue(error.message || 'GraphQL mutation failed');
    }
  }
);

export const updateAsset = createAsyncThunk(
  'asset/updateAsset',
  async (updateData, { rejectWithValue }) => {
    try {
      const { data } = await client.mutate({
        mutation: UPDATE_ASSET,
        variables: {
          ...updateData,
          cost: updateData.cost ? parseFloat(updateData.cost) : undefined,
          warrantyDuration: updateData.warrantyDuration ? parseInt(updateData.warrantyDuration) : undefined,
        },
      });

      return data.updateAsset;
    } catch (error) {
      console.error('GraphQL Error (updateAsset):', error);
      return rejectWithValue(error.message || 'Failed to update asset');
    }
  }
);

export const softDeleteAsset = createAsyncThunk(
  'asset/softDeleteAsset',
  async ({ id, userName, editedAt }, { rejectWithValue }) => {
    try {
      const { data } = await client.mutate({
        mutation: SOFT_DELETE_ASSET_RECORD,
        variables: {
          id,
          userName,
          editedAt,
        },
      });

      if (data?.softDeleteAssetRecord?.id) {
        return { id: data.softDeleteAssetRecord.id, status: data.softDeleteAssetRecord.status };
      } else {
        return rejectWithValue('Failed to delete asset');
      }
    } catch (error) {
      console.error('GraphQL Error (softDeleteAsset):', error);
      return rejectWithValue(error.message || 'Failed to delete asset');
    }
  }
);

const assetSlice = createSlice({
  name: 'asset',
  initialState: {
    allAssets: {
      records: [],
      totalCount: 0
    },
    assetHistory: [],
    assetAttachments: [],
  },
  reducers: {
    setAllAssets: (state, action) => {
      state.allAssets = {
        records: action.payload.records || [],
        totalCount: action.payload.totalCount || 0
      };
    },

    setAssetHistory: (state, action) => {
      state.assetHistory = action.payload;
    },
    resetAssetStatus: (state) => {
      state.status = null;
    },
    setAssetAttachments: (state, action) => {
      state.assetAttachments = action.payload;
    },

  },
  extraReducers: (builder) => {
    builder.addCase(addAssetEntry.fulfilled, (state, action) => {
      if (Array.isArray(state.allAssets?.records)) {
        state.allAssets.records.unshift(action.payload);
        state.allAssets.totalCount += 1;
      }
    });
    
    builder.addCase(updateAsset.fulfilled, (state, action) => {
      const index = state.allAssets.records?.findIndex(asset => asset.id === action.payload.id);
      if (index !== -1 && state.allAssets.records) {
        // Update the specific asset in the records array
        state.allAssets.records[index] = { ...state.allAssets.records[index], ...action.payload };
      }
    });
    
    builder.addCase(softDeleteAsset.fulfilled, (state, action) => {
      if (state.allAssets.records) {
        // Remove the deleted asset from the records array
        state.allAssets.records = state.allAssets.records.filter(asset => asset.id !== action.payload.id);
        // Update total count if it exists
        if (state.allAssets.totalCount) {
          state.allAssets.totalCount -= 1;
        }
      }
    });
  },
});

export const { setAllAssets, setAssetHistory, resetAssetStatus, setAssetAttachments } = assetSlice.actions;
export default assetSlice.reducer;
